export class AppState {
  constructor() {
    this.data = {
      // Navigation
      currentPage: "config-global",

      // Status des sources de données
      sources: {
        configTiroirs: { status: "empty", data: null, errors: [] },
        baseROP: { status: "empty", data: null, errors: [], selectedSheet: null, selectedColumns: {} },
        rapportFiberPvReussite: { status: "empty", data: null, errors: [] },
        pvReussite: { status: "empty", data: null, errors: [] },
        rapportFiberPvEchec: { status: "empty", data: null, errors: [] },
        pvEchec: { status: "empty", data: null, errors: [] },
        pvHs: { status: "empty", data: null, errors: [] },
        conflictResolutions: { status: "empty", data: null, errors: [] },
      },
      BaseRopExcelFileParams: {
        workBook: null,
        sheet: null,
        referenceColumn: null,
        portColumn: null,
        bpoColumn: null,
        originalFile: null,
      },
      // Résultats d'analyse
      analysis: {
        isComplete: false,
        results: null,
        report: null,
      },

      // Toaster messages
      toaster: {
        messages: [],
      },
    };

    // Event listeners
    this.listeners = new Map();

    this.initToaster();
  }

  // ===== GESTION DES ÉVÉNEMENTS =====

  on(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, []);
    }
    this.listeners.get(event).push(callback);
  }

  emit(event, data) {
    if (this.listeners.has(event)) {
      this.listeners.get(event).forEach((callback) => callback(data));
    }
  }

  // ===== NAVIGATION =====

  setCurrentPage(pageId) {
    this.data.currentPage = pageId;
    this.emit("pageChanged", pageId);
    this.emit("stateChanged");
  }

  getCurrentPage() {
    return this.data.currentPage;
  }

  // ===== GESTION DES SOURCES =====

  updateSource(sourceKey, updates) {
    if (this.data.sources[sourceKey]) {
      this.data.sources[sourceKey] = {
        ...this.data.sources[sourceKey],
        ...updates,
      };
      this.emit("sourceUpdated", { source: sourceKey, data: this.data.sources[sourceKey] });
      this.emit("stateChanged");
    }
  }

  getSource(sourceKey) {
    return this.data.sources[sourceKey] || null;
  }

  getAllSources() {
    return this.data.sources;
  }

  // Status helpers
  setSourceLoading(sourceKey) {
    this.updateSource(sourceKey, { status: "loading", errors: [] });
  }

  setSourceSuccess(sourceKey, data) {
    this.updateSource(sourceKey, { status: "success", data, errors: [] });
  }

  setSourceError(sourceKey, errors) {
    this.updateSource(sourceKey, { status: "error", errors });
  }

  setSourceEmpty(sourceKey) {
    this.updateSource(sourceKey, { status: "empty", data: null, errors: [] });
  }

  // ===== GESTION fichier excel rop =====

  // Sauvegarder le conteu du fichier base rop
  setBaseRopWorkBookParams(workBook, sheet, referenceColumn, portColumn, bpoColumn, originalFile = null) {
    this.data.BaseRopExcelFileParams.workBook = workBook;
    this.data.BaseRopExcelFileParams.sheet = sheet;
    this.data.BaseRopExcelFileParams.referenceColumn = referenceColumn;
    this.data.BaseRopExcelFileParams.portColumn = portColumn;
    this.data.BaseRopExcelFileParams.bpoColumn = bpoColumn;
    this.data.BaseRopExcelFileParams.originalFile = originalFile;
    this.emit("ExcelBaseRop-WorkBookParamsSet", { source: "baseROP", workBook });
  }

  // Récupérer le fichier original
  getBaseRopWorkBookParams() {
    return this.data.BaseRopExcelFileParams;
  }

  // ===== GESTION TOASTER =====

  initToaster() {
    this.addToasterMessage("info", "Application initialisée");
    this.addToasterMessage("info", "En attente des fichiers sources");
  }

  addToasterMessage(level, message) {
    const timestamp = new Date().toLocaleTimeString("fr-FR", {
      hour12: false,
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });

    const toasterMessage = {
      id: Date.now() + Math.random(),
      timestamp,
      level,
      message,
      time: new Date(),
    };

    this.data.toaster.messages.push(toasterMessage);

    // Limiter à 100 messages max
    if (this.data.toaster.messages.length > 100) {
      this.data.toaster.messages.shift();
    }

    this.emit("toasterUpdated", toasterMessage);
  }

  getToasterMessages() {
    return this.data.toaster.messages;
  }

  clearToaster() {
    this.data.toaster.messages = [];
    this.emit("toasterCleared");
    this.addToasterMessage("info", "Messages effacés");
  }

  // ===== VALIDATION GLOBALE =====

  canRunAnalysis() {
    const sources = this.data.sources;
    return (
      sources.configTiroirs.status === "success" &&
      sources.baseROP.status === "success" &&
      sources.rapportFiberPvReussite.status === "success" &&
      sources.pvReussite.status === "success" &&
      sources.rapportFiberPvEchec.status === "success" &&
      sources.pvEchec.status === "success" &&
      sources.pvHs.status === "success"
    );
  }

  getGlobalStatus() {
    const sources = this.data.sources;
    const statuses = Object.values(sources).map((source) => source.status);

    if (statuses.includes("error")) return "error";
    if (statuses.includes("loading")) return "loading";
    if (statuses.every((status) => status === "success")) return "ready";
    return "incomplete";
  }

  // ===== BASE ROP SPECIFIQUE =====

  setBaseRopSheet(sheetName) {
    this.updateSource("baseROP", { selectedSheet: sheetName });
    this.addToasterMessage("info", `Feuille "${sheetName}" sélectionnée`);
  }

  setBaseRopColumns(columns) {
    this.updateSource("baseROP", { selectedColumns: columns });
    this.addToasterMessage("info", `Colonnes configurées`);
  }

  // ===== ANALYSE =====

  setAnalysisResults(results) {
    this.data.analysis = {
      isComplete: true,
      results,
      report: null,
    };
    this.emit("analysisComplete", results);
    this.emit("stateChanged");
    this.addToasterMessage("success", `${results.totalFibers} fibres analysées`);
  }

  // ===== RESET =====

  reset() {
    // Garder seulement la page courante
    const currentPage = this.data.currentPage;

    this.data = {
      currentPage,
      sources: {
        configTiroirs: { status: "empty", data: null, errors: [] },
        baseROP: { status: "empty", data: null, errors: [], selectedSheet: null, selectedColumns: {} },
        rapportFiberPvReussite: { status: "empty", data: null, errors: [] },
        pvReussite: { status: "empty", data: null, errors: [] },
        rapportFiberPvEchec: { status: "empty", data: null, errors: [] },
        pvEchec: { status: "empty", data: null, errors: [] },
        pvHs: { status: "empty", data: null, errors: [] },
        conflictResolutions: { status: "empty", data: null, errors: [] },
      },
      analysis: {
        isComplete: false,
        results: null,
        report: null,
      },
      BaseRopExcelFileParams: {
        workBook: null,
        sheet: null,
        referenceColumn: null,
        portColumn: null,
        bpoColumn: null,
        originalFile: null,
      },
      toaster: {
        messages: [],
      },
    };

    this.emit("stateReset");
    this.emit("stateChanged");
    this.initToaster();
  }

  // ===== DEBUG =====

  getState() {
    return JSON.parse(JSON.stringify(this.data));
  }

  logState() {}
}
